/**
 * Socket.IO Signaling Client - Enables cross-browser WebRTC connections
 * Connects to signaling server for WebRTC offer/answer/ICE exchange
 * Migrated from raw WebSockets to Socket.IO for enhanced reliability
 */

import { io, Socket } from 'socket.io-client';

export interface SignalingMessage {
  type: string;
  [key: string]: any;
}

export interface SignalingEvents {
  connected: (data: { clientId: string }) => void;
  'party-created': (data: { partyId: string; hostId: string }) => void;
  'party-joined': (data: { partyId: string; playerId: string; playerName: string; assetManifest?: any }) => void;
  'join-error': (data: { error: string; errorCode?: string }) => void;
  'player-waitlisted': (data: { position: number; estimatedWaitTime: number; queueLength: number }) => void;
  'player-joined': (data: { playerId: string; playerName: string }) => void;
  'player-left': (data: { playerId: string }) => void;
  'party-message': (data: { data: any; senderId?: string; timestamp?: number }) => void;
  'party-config': (data: any) => void;
  'player-loading-state': (data: { playerId: string; playerName: string; isReady: boolean; overallProgress: number }) => void;
  'party-state-update': (data: any) => void;
  'player-state-update': (data: any) => void;
  'host-message': (data: any) => void;
  'game-started': (data: any) => void;
  'round-started': (data: any) => void;
  'round-ended': (data: any) => void;
  'pseudo-options': (data: { options: any[] }) => void;
  'pseudo-confirmed': (data: { confirmed: boolean }) => void;
  'webrtc-offer': (data: { senderId: string; offer: RTCSessionDescription }) => void;
  'webrtc-answer': (data: { senderId: string; answer: RTCSessionDescription }) => void;
  'ice-candidate': (data: { senderId: string; candidate: RTCIceCandidate }) => void;
  error: (data: { error: string }) => void;
  disconnected: () => void;
}

/**
 * Socket.IO-based signaling client for WebRTC handshake (Singleton)
 * Enhanced reliability with automatic reconnection and mobile browser support
 */
export class WebSocketSignaling {
  private static instance: WebSocketSignaling | null = null;

  private socket: Socket | null = null;
  private serverUrl: string;
  private clientId: string | null = null;
  private partyId: string | null = null;
  private isHost: boolean = false;
  private eventHandlers = new Map<string, Function[]>();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private isConnecting: boolean = false;
  private connectionPromise: Promise<void> | null = null;
  private _isConnected: boolean = false; // Internal reactive state

  private constructor(serverUrl?: string) {
    console.log('Construct WebSocketSignaling');
    // Auto-detect server URL based on current location
    if (!serverUrl) {
      // Always use wss:// for secure WebSocket connection when page is HTTPS
      // This ensures Web APIs that require secure context work correctly
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const hostname = window.location.hostname;
      const port = '8080';
      this.serverUrl = `${protocol}//${hostname}:${port}`;

      if (hostname.match('multiparty')) {
        this.serverUrl = `https://www.multiparty.site/`;
      }

      console.log(`[WebSocketSignaling] Auto-detected server URL: ${this.serverUrl}`);
    } else {
      this.serverUrl = serverUrl;
      console.log(`[WebSocketSignaling] Using provided server URL: ${this.serverUrl}`);
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(serverUrl?: string): WebSocketSignaling {
    if (!window['__WebSocketSignalingInstance']) {
      window['__WebSocketSignalingInstance'] = new WebSocketSignaling(serverUrl);
    }
    return window['__WebSocketSignalingInstance'];
  }

  /**
   * Reset singleton instance (for testing or complete reset)
   */
  static resetInstance(): void {
    if (window['__WebSocketSignalingInstance']) {
      window['__WebSocketSignalingInstance'].disconnect();
      window['__WebSocketSignalingInstance'] = null;
    }
  }

  /**
   * Connect to signaling server
   */
  async connect(): Promise<void> {
    console.log('[WebSocketSignaling] connect() called');

    // If already connected, return immediately
    if (this.socket && this.socket.connected) {
      console.log('[WebSocketSignaling] Already connected');
      return;
    }

    // If connection is in progress, return the existing promise
    if (this.connectionPromise) {
      console.log('[WebSocketSignaling] Connection already in progress, waiting...');
      return this.connectionPromise;
    }

    this.isConnecting = true;
    console.log('[WebSocketSignaling] Starting connection process');

    this.connectionPromise = new Promise((resolve, reject) => {
      this.socket = io(this.serverUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay
      });

      this.socket.on('connect', () => {
        console.log('[WebSocketSignaling] Connected to signaling server');
        this.isConnecting = false;
        this.connectionPromise = null;
        this.reconnectAttempts = 0;
        this._isConnected = true;
        this.emit('connected', { clientId: 'pending' });
        resolve();
      });

      this.socket.on('message', (message: SignalingMessage) => {
        try {
          console.log('[WebSocketSignaling] Parsed message:', message);
          this.handleMessage(message);
        } catch (error) {
          console.error('[WebSocketSignaling] Invalid message:', error);
        }
      });

      this.socket.on('disconnect', (reason: string) => {
        console.log('[WebSocketSignaling] Disconnected:', reason);
        this.isConnecting = false;
        this.connectionPromise = null;
        this._isConnected = false;
        this.emit('disconnected');

        // Socket.IO handles reconnection automatically
      });

      this.socket.on('connect_error', (error) => {
        console.error('[WebSocketSignaling] Socket.IO error:', error);
        this.isConnecting = false;
        this.connectionPromise = null;
        this._isConnected = false;
        reject(error);
      });
    });

    return this.connectionPromise;
  }

  /**
   * Disconnect from signaling server
   */
  disconnect(): void {
    console.log('[WebSocketSignaling] disconnect() called');
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this._isConnected = false;
    this.clientId = null;
    this.partyId = null;
  }

  /**
   * Create a new party (host)
   */
  async createParty(partyId: string): Promise<void> {
    await this.ensureConnected();

    this.isHost = true;
    this.partyId = partyId;

    this.sendMessage({
      type: 'host-message',
      data: {
        type: 'create-party',
        partyId
      }
    });
  }

  /**
   * Create a new party with configuration (host)
   */
  async createPartyWithConfig(partyId: string, configuration: any): Promise<void> {
    await this.ensureConnected();

    this.isHost = true;
    this.partyId = partyId;

    this.sendMessage({
      type: 'host-message',
      data: {
        type: 'create-party',
        partyId,
        configuration
      }
    });
  }

  /**
   * Get party configuration from server
   */
  async getPartyConfig(partyId: string): Promise<void> {
    await this.ensureConnected();

    this.sendMessage({
      type: 'get-party-config',
      partyId
    });
  }

  /**
   * Join an existing party (player)
   */
  async joinParty(partyId: string, playerData: string | { name: string; pseudo?: string; avatar?: string; firstName?: string }): Promise<void> {
    await this.ensureConnected();

    this.isHost = false;
    this.partyId = partyId;

    // Support both string (legacy) and object (new) formats
    if (typeof playerData === 'string') {
      this.sendMessage({
        type: 'player-message',
        data: {
          type: 'join-party',
          partyId,
          playerName: playerData
        }
      });
    } else {
      this.sendMessage({
        type: 'player-message',
        data: {
          type: 'join-party',
          partyId,
          playerName: playerData.name,
          pseudo: playerData.pseudo,
          avatar: playerData.avatar,
          firstName: playerData.firstName
        }
      });
    }
  }

  /**
   * Leave current party
   */
  leaveParty(): void {
    if (this.partyId) {
      this.sendMessage({
        type: 'player-message',
        data: {
          type: 'leave-party'
        }
      });
      this.partyId = null;
      this.isHost = false;
    }
  }

  /**
   * Send WebRTC offer
   */
  sendOffer(targetId: string, offer: RTCSessionDescription): void {
    this.sendMessage({
      type: 'webrtc-offer',
      targetId,
      offer
    });
  }

  /**
   * Send WebRTC answer
   */
  sendAnswer(targetId: string, answer: RTCSessionDescription): void {
    this.sendMessage({
      type: 'webrtc-answer',
      targetId,
      answer
    });
  }

  /**
   * Send ICE candidate
   */
  sendIceCandidate(targetId: string, candidate: RTCIceCandidate): void {
    this.sendMessage({
      type: 'ice-candidate',
      targetId,
      candidate
    });
  }

  /**
   * Send message to party members
   */
  sendPartyMessage(message: any): void {
    this.sendMessage({
      type: 'party-message',
      data: message
    });
  }

  /**
   * Send message directly to server (same as joinParty uses)
   */
  sendDirectMessage(message: SignalingMessage): void {
    this.sendMessage(message);
  }

  /**
   * Send player message (wrapped in player-message format)
   */
  sendPlayerMessage(data: any): void {
    this.sendMessage({
      type: 'player-message',
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Send host message (wrapped in host-message format)
   */
  sendHostMessage(data: any): void {
    this.sendMessage({
      type: 'host-message',
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Add event listener
   */
  on<K extends keyof SignalingEvents>(event: K, handler: SignalingEvents[K]): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler as Function);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof SignalingEvents>(event: K, handler: SignalingEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler as Function);
      if (index >= 0) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Get connection status
   */
  get isConnected(): boolean {
    const socketConnected = this.socket !== null && this.socket.connected;

    // Sync internal flag with actual Socket.IO state if there's a mismatch
    if (socketConnected && !this._isConnected) {
      console.log('[WebSocketSignaling] Syncing _isConnected flag with actual Socket.IO state');
      this._isConnected = true;
    } else if (!socketConnected && this._isConnected) {
      this._isConnected = false;
    }

    return socketConnected;
  }

  get currentClientId(): string | null {
    return this.clientId;
  }

  get currentPartyId(): string | null {
    return this.partyId;
  }

  // ===== PRIVATE METHODS =====

  private async ensureConnected(): Promise<void> {
    if (!this.isConnected) {
      await this.connect();
    }
  }

  private sendMessage(message: SignalingMessage): void {
    console.log('[WebSocketSignaling] Sending message:', message);

    if (!this.socket || !this.socket.connected) {
      console.error('[WebSocketSignaling] Cannot send message: not connected');
      return;
    }

    try {
      this.socket.emit('message', {
        ...message,
        timestamp: Date.now()
      });
      console.log('[WebSocketSignaling] Message sent successfully');
    } catch (error) {
      console.error('[WebSocketSignaling] Send error:', error);
    }
  }

  private handleMessage(message: SignalingMessage): void {
    console.log(`[WebSocketSignaling] Received: ${message.type}`);

    // Handle special messages
    if (message.type === 'connected') {
      this.clientId = message.clientId;
    }

    // Emit event to listeners
    this.emit(message.type, message);
  }

  private emit(event: string, data?: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`[WebSocketSignaling] Event handler error for ${event}:`, error);
        }
      });
    }
  }
}
